import { BuyLinkEditor } from '../../BuyLinks'
import { shapeOf } from '../itemDraft'
import type { Draft, Problems } from '../itemDraft'
import { EditorSection, Field } from './controls'

/**
 * Where it comes from and how much to keep — the answers somebody needs when
 * the shelf runs low. The minimum is what turns a tile's bar amber in
 * Inventory (`stockLevel`), which is why it lives here and not in Design.
 *
 * Buy links are for bought parts only (§4.2): a part we design has no
 * supplier page to link to, only a vendor and their quote.
 */
export function BuyingSection({
  draft,
  set,
  problems,
}: {
  draft: Draft
  set: (patch: Partial<Draft>) => void
  problems: Problems
}): JSX.Element {
  const { kind, tracking } = shapeOf(draft)
  const unit = tracking === 'quantity' && draft.unit.trim() ? draft.unit.trim() : 'each'
  const sourced = kind !== 'make_internal'

  return (
    <EditorSection
      title={sourced ? 'Buying' : 'Stock'}
      intro={sourced ? 'Who supplies it, where to reorder, and how many to keep.' : 'How long one takes to build, and how many to keep ready.'}
    >
      {sourced ? (
        <div className="ie-row">
          <Field label={kind === 'buy' ? 'Supplier' : 'Vendor'}>
            <input value={draft.supplier} onChange={(e) => set({ supplier: e.target.value })} placeholder={kind === 'buy' ? 'e.g. Stereolabs' : 'e.g. Protolabs'} />
          </Field>
          <Field label={kind === 'buy' ? 'Their part number' : 'Drawing or quote number'}>
            <input value={draft.partNumber} onChange={(e) => set({ partNumber: e.target.value })} autoCapitalize="characters" spellCheck={false} />
          </Field>
        </div>
      ) : null}

      {kind === 'buy' ? (
        <div className="ie-field ie-field--wide">
          <span className="ie-field__label">Where to buy it</span>
          <BuyLinkEditor links={draft.purchaseLinks} onChange={(links) => set({ purchaseLinks: [...links] })} />
        </div>
      ) : null}

      <div className="ie-row">
        <Field
          label={sourced ? 'Lead time' : 'Build time'}
          error={problems.leadTimeDays}
          note={sourced ? 'Order to arrival. Leave blank rather than guess.' : 'Start to finished. Leave blank rather than guess.'}
        >
          <span className="ie-suffixed">
            <input inputMode="numeric" value={draft.leadTimeDays} onChange={(e) => set({ leadTimeDays: e.target.value })} placeholder="—" />
            <span className="ie-suffixed__unit">days</span>
          </span>
        </Field>
        <Field label="Minimum on the shelf" error={problems.safetyStock} note={sourced ? 'At or below this, Inventory marks it Low stock.' : 'How many to keep ready. Planning reads it.'}>
          <span className="ie-suffixed">
            <input inputMode="decimal" value={draft.safetyStock} onChange={(e) => set({ safetyStock: e.target.value })} placeholder="Not set" />
            <span className="ie-suffixed__unit">{unit}</span>
          </span>
        </Field>
      </div>
    </EditorSection>
  )
}
